import { Clock, List, MapPin, Phone, ShoppingBag, UserCircle, X } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useCustomer } from "../context/CustomerContext";
import { navLinks, siteInfo } from "../data/siteData";
import { BrandMark } from "./BrandMark";
import { MagneticButton } from "./MagneticButton";

function formatPrice(value: number) {
  return `${value.toLocaleString("ru-RU")} ₽`;
}

export function SiteHeader() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { itemCount, total } = useCart();
  const { profile, activeOrder } = useCustomer();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-40 px-4 pt-4">
      <div className="mx-auto hidden max-w-[1440px] items-center justify-between gap-4 rounded-full border border-white/60 bg-white/36 px-5 py-2 text-sm text-stone-600 backdrop-blur-lg lg:flex">
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-2">
            <Clock size={16} className="text-teal-700" />
            {siteInfo.hours}
          </span>
          <a
            href={siteInfo.mapHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 transition duration-300 hover:text-stone-900"
          >
            <MapPin size={16} className="text-teal-700" />
            {siteInfo.address}
          </a>
        </div>
        <a
          href={siteInfo.phoneHref}
          className="inline-flex items-center gap-2 font-medium text-stone-900 transition duration-300 hover:text-teal-900"
        >
          <Phone size={16} className="text-teal-700" />
          {siteInfo.phone}
        </a>
      </div>

      <motion.div
        layout
        className="relative mx-auto mt-3 flex w-full max-w-[1440px] items-center justify-between gap-4 rounded-[2rem] border border-white/72 bg-[rgba(255,250,243,0.78)] px-4 py-3 shadow-[0_30px_78px_-48px_rgba(27,53,52,0.5)] backdrop-blur-2xl"
      >
        <Link
          to="/"
          className="flex min-w-0 flex-1 items-center gap-3 rounded-[1.4rem] border border-teal-900/8 bg-white/60 px-3 py-2 sm:flex-none"
        >
          <BrandMark />
          <span className="min-w-0 max-w-[8.8rem] sm:max-w-[13rem] md:max-w-none">
            <span className="block text-[0.92rem] leading-[1.05] font-semibold text-stone-950 sm:text-sm md:text-base">
              {siteInfo.brand}
            </span>
            <span className="hidden truncate text-xs text-stone-500 md:block md:text-sm">
              {siteInfo.subtitle}
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 rounded-full border border-white/60 bg-white/44 p-1 lg:flex">
          {navLinks.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  "relative rounded-full px-4 py-2 text-sm font-medium tracking-tight transition duration-300",
                  isActive ? "bg-teal-900/[0.07] text-stone-950" : "text-stone-500 hover:text-stone-900"
                ].join(" ")
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/account"
            className="inline-flex items-center gap-2 rounded-full border border-teal-900/10 bg-white/70 px-4 py-2 text-sm font-medium text-stone-900 shadow-[0_18px_38px_-30px_rgba(27,53,52,0.38)] transition duration-300 hover:bg-white"
          >
            <UserCircle size={16} className="text-teal-700" />
            {activeOrder ? `${profile.firstName} • ${activeOrder.shortStatus}` : profile.firstName}
          </Link>
          {itemCount > 0 ? (
            <Link
              to="/order"
              className="inline-flex items-center gap-2 rounded-full border border-teal-900/10 bg-white/70 px-4 py-2 text-sm font-medium text-stone-900 shadow-[0_18px_38px_-30px_rgba(27,53,52,0.38)] transition duration-300 hover:bg-white"
            >
              <ShoppingBag size={16} className="text-teal-700" />
              {itemCount} • {formatPrice(total)}
            </Link>
          ) : null}

          <MagneticButton to="/order" variant="primary" size="md">
            Оформить заказ
          </MagneticButton>
        </div>

        <button
          type="button"
          aria-expanded={open}
          aria-controls="mobile-navigation"
          aria-label={open ? "Закрыть меню" : "Открыть меню"}
          data-testid="nav-toggle"
          onClick={() => setOpen((current) => !current)}
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-teal-900/10 bg-white/72 px-3.5 py-3 text-sm font-medium text-stone-900 shadow-[0_18px_38px_-28px_rgba(27,53,52,0.46)] transition duration-300 hover:bg-white lg:hidden"
        >
          {open ? <X size={20} /> : <List size={20} />}
          <span>Меню</span>
        </button>

        <AnimatePresence>
          {open ? (
            <motion.div
              id="mobile-navigation"
              initial={{ opacity: 0, y: -16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              className="absolute left-0 right-0 top-[calc(100%+0.8rem)] grid gap-3 rounded-[2rem] border border-white/70 bg-[rgba(255,249,242,0.96)] p-4 shadow-[0_30px_82px_-44px_rgba(27,53,52,0.56)] backdrop-blur-2xl lg:hidden"
            >
              {navLinks.map((item, index) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.04 * index, duration: 0.22 }}
                >
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      [
                        "block rounded-[1.25rem] px-4 py-3 text-base font-medium tracking-tight transition duration-300",
                        isActive
                          ? "bg-teal-900/[0.06] text-stone-950"
                          : "bg-white/60 text-stone-600 hover:bg-white hover:text-stone-950"
                      ].join(" ")
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}

              <div className="rounded-[1.5rem] border border-teal-900/10 bg-white/72 p-4 text-sm leading-7 text-stone-600">
                <p className="font-medium text-stone-950">{siteInfo.hours}</p>
                <Link
                  to="/account"
                  className="mt-2 inline-flex items-center gap-2 font-medium text-teal-800"
                >
                  <UserCircle size={16} />
                  {activeOrder ? `Кабинет • ${activeOrder.shortStatus}` : "Личный кабинет"}
                </Link>
                <a
                  href={siteInfo.mapHref}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-start gap-2 transition duration-300 hover:text-stone-950"
                >
                  <MapPin size={16} className="mt-1 text-teal-700" />
                  <span>{siteInfo.address}</span>
                </a>
                <a
                  href={siteInfo.phoneHref}
                  className="mt-2 inline-flex items-center gap-2 font-medium text-teal-800"
                >
                  <Phone size={16} />
                  {siteInfo.phone}
                </a>
              </div>

              <MagneticButton to="/order" fullWidth>
                Перейти к заказу
              </MagneticButton>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.div>
    </header>
  );
}
